import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import Text from '../Text';
import { Feather } from '../Icons';
import { useStyles } from '~/hooks';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    // Translated via the core Text component (i18n keys or plain text).
    title: string;
    subtitle?: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
};

// Collapsible section with a chevron header — advanced/optional parameter groups.
export default function Expander({ title, subtitle, defaultOpen = false, children }: Props) {
    const styles = useStyles(stylesGenerator);
    const [open, setOpen] = useState(defaultOpen);

    return (
        <View style={styles.container}>
            <Pressable
                onPress={() => setOpen((prev) => !prev)}
                style={({ pressed }) => [styles.headerRow, pressed && styles.headerRowPressed]}
            >
                <View style={styles.headerTextWrap}>
                    <Text style={styles.title}>{title}</Text>
                    {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
                </View>
                <Feather name={open ? 'chevron-up' : 'chevron-down'} size={scaleSize(18)} style={styles.chevron} />
            </Pressable>
            {open ? <View style={styles.content}>{children}</View> : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            marginBottom: scaleSize(10),
        },
        headerRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: scaleSize(12),
            paddingVertical: scaleSize(11),
        },
        headerRowPressed: {
            opacity: 0.7,
        },
        headerTextWrap: {
            flex: 1,
            marginRight: scaleSize(8),
        },
        title: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        subtitle: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginTop: scaleSize(1),
        },
        chevron: {
            color: theme.textSecondary,
        },
        content: {
            paddingHorizontal: scaleSize(12),
            paddingBottom: scaleSize(12),
        },
    });
